import { useState } from 'react'
import { api } from '../../lib/api'
import './SupportForm.css'

const PRIORITIES = ['Low', 'Medium', 'High', 'Critical'] as const
const ISSUE_TYPES = ['Time Entry', 'Payroll', 'Impacting DSP', 'Billing', 'Report']
const WHO_IMPACTS = ['DSP', 'Admin', 'Customer', 'Customer and DSP', 'All of the above']
const HUB_SECTIONS = ['Billing', 'Payroll', 'Contracts', 'Time Entries', 'Customer']

export function SupportForm() {
  const [step, setStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(false)

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [company, setCompany] = useState('')
  const [submissionType, setSubmissionType] = useState<'issue' | 'enhancement'>('issue')
  const [priority, setPriority] = useState('')
  const [urlScreen, setUrlScreen] = useState('')
  const [whatTrying, setWhatTrying] = useState('')
  const [screenshot, setScreenshot] = useState<File | null>(null)
  const [internalError, setInternalError] = useState('')
  const [timeOccurred, setTimeOccurred] = useState('')
  const [typeOfIssue, setTypeOfIssue] = useState<string[]>([])
  const [whoImpacts, setWhoImpacts] = useState('')
  const [businessPurpose, setBusinessPurpose] = useState('')
  const [businessImpact, setBusinessImpact] = useState('')
  const [urlNavImpact, setUrlNavImpact] = useState('')
  const [featureByDate, setFeatureByDate] = useState('')
  const [whoTesting, setWhoTesting] = useState('')
  const [additionalNotes, setAdditionalNotes] = useState('')
  const [hubSections, setHubSections] = useState<string[]>([])

  const toggleIssueType = (v: string) => {
    setTypeOfIssue((prev) =>
      prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]
    )
  }
  const toggleHubSection = (v: string) => {
    setHubSections((prev) =>
      prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]
    )
  }

  const totalSteps = 3
  const canNextStep1 = name.trim() && email.trim() && company.trim()
  const canNextStep2 = submissionType === 'issue' || submissionType === 'enhancement'
  const canSubmitIssue = !!priority
  const canSubmitEnhancement =
    priority &&
    businessPurpose.trim() &&
    businessImpact.trim() &&
    urlNavImpact.trim() &&
    featureByDate &&
    whoTesting.trim()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (submissionType === 'issue' && !canSubmitIssue) return
    if (submissionType === 'enhancement' && !canSubmitEnhancement) return
    setLoading(true)
    setError(false)
    try {
      const form = new FormData()
      form.append('name', name.trim())
      form.append('email', email.trim())
      form.append('company', company.trim())
      form.append('submission_type', submissionType)
      form.append('priority', priority)
      if (submissionType === 'issue') {
        form.append('url_screen', urlScreen)
        form.append('what_trying', whatTrying)
        form.append('internal_error', internalError)
        form.append('time_occurred', timeOccurred)
        form.append('type_of_issue', typeOfIssue.join(', '))
        form.append('who_impacts', whoImpacts)
        if (screenshot) form.append('screenshot', screenshot)
      } else {
        form.append('business_purpose', businessPurpose)
        form.append('business_impact', businessImpact)
        form.append('url_nav_impact', urlNavImpact)
        form.append('feature_by_date', featureByDate)
        form.append('who_testing', whoTesting)
        form.append('additional_notes', additionalNotes)
        form.append('hub_sections', hubSections.join(', '))
      }
      await api.post('/api/support/submit', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      setSuccess(true)
    } catch {
      setError(true)
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="support-form" style={{ padding: '2rem', backgroundColor: 'var(--bg-3)', borderRadius: '8px' }}>
        <h3 className="font-display font-bold text-lg uppercase text-white mb-4">Thank you</h3>
        <p className="font-body text-cream-dim">
          Your submission has been received. We will review it and get back to you as needed.
        </p>
      </div>
    )
  }

  return (
    <form className="support-form" onSubmit={handleSubmit}>
      <div className="step-indicator">
        <span className={`dot ${step >= 1 ? (step > 1 ? 'done' : 'active') : ''}`} />
        <span className={`dot ${step >= 2 ? (step > 2 ? 'done' : 'active') : ''}`} />
        <span className={`dot ${step >= 3 ? 'active' : ''}`} />
        <span style={{ marginLeft: '0.5rem' }}>
          Step {step} of {totalSteps}
        </span>
      </div>

      {error && (
        <div
          className="field-group"
          style={{
            padding: '0.75rem 1rem',
            borderRadius: '6px',
            borderLeft: '4px solid #ef4444',
            background: 'var(--bg-3)',
          }}
        >
          <p className="font-body text-cream-dim">
            Something went wrong. Please try again or contact support directly.
          </p>
        </div>
      )}

      {step === 1 && (
        <>
          <div className="field-group">
            <label htmlFor="hub-name">What is your name? *</label>
            <input
              id="hub-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="field-group">
            <label htmlFor="hub-email">What is your email? *</label>
            <input
              id="hub-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="field-group">
            <label htmlFor="hub-company">What company are you with? *</label>
            <input
              id="hub-company"
              type="text"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              required
            />
          </div>
          <div className="btn-row">
            <button
              type="button"
              className="btn-primary"
              disabled={!canNextStep1}
              onClick={() => setStep(2)}
            >
              Next
            </button>
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <div className="field-group">
            <label>Are you reporting an issue or requesting an enhancement? *</label>
            <div className="radio-group">
              <label>
                <input
                  type="radio"
                  name="submission_type"
                  checked={submissionType === 'issue'}
                  onChange={() => setSubmissionType('issue')}
                />
                <span>Reporting an issue (something in the software that is not working)</span>
              </label>
              <label>
                <input
                  type="radio"
                  name="submission_type"
                  checked={submissionType === 'enhancement'}
                  onChange={() => setSubmissionType('enhancement')}
                />
                <span>Requesting an enhancement (something that is new in the software)</span>
              </label>
            </div>
          </div>
          <div className="btn-row">
            <button type="button" className="btn-secondary" onClick={() => setStep(1)}>
              Back
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => setStep(3)}
            >
              Next
            </button>
          </div>
        </>
      )}

      {step === 3 && submissionType === 'issue' && (
        <>
          <h3 className="font-display font-bold text-lg uppercase text-white mb-4">Submit a Bug or Issue</h3>
          <div className="field-group">
            <label htmlFor="hub-priority">Priority *</label>
            <select
              id="hub-priority"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              required
            >
              <option value="">Select</option>
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          <div className="field-group">
            <label htmlFor="hub-url-screen">What is the URL of the screen you are at when this error comes in?</label>
            <input
              id="hub-url-screen"
              type="text"
              value={urlScreen}
              onChange={(e) => setUrlScreen(e.target.value)}
            />
          </div>
          <div className="field-group">
            <label htmlFor="hub-what-trying">What are you trying to do?</label>
            <textarea
              id="hub-what-trying"
              value={whatTrying}
              onChange={(e) => setWhatTrying(e.target.value)}
              rows={3}
            />
          </div>
          <div className="field-group">
            <label htmlFor="hub-screenshot">Upload Screenshot</label>
            <input
              id="hub-screenshot"
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              onChange={(e) => setScreenshot(e.target.files?.[0] ?? null)}
            />
            <p className="file-hint">Images only: PNG, JPEG, GIF, WebP</p>
          </div>
          <div className="field-group">
            <label>Did you get an internal server error?</label>
            <div className="radio-group">
              <label>
                <input
                  type="radio"
                  name="internal_error"
                  checked={internalError === 'Yes'}
                  onChange={() => setInternalError('Yes')}
                />
                <span>Yes</span>
              </label>
              <label>
                <input
                  type="radio"
                  name="internal_error"
                  checked={internalError === 'No'}
                  onChange={() => setInternalError('No')}
                />
                <span>No</span>
              </label>
            </div>
          </div>
          <div className="field-group">
            <label htmlFor="hub-time-occurred">What time did this issue occur?</label>
            <input
              id="hub-time-occurred"
              type="datetime-local"
              value={timeOccurred}
              onChange={(e) => setTimeOccurred(e.target.value)}
            />
          </div>
          <div className="field-group">
            <label>Select Type of Issue</label>
            <div className="checkbox-group">
              {ISSUE_TYPES.map((t) => (
                <label key={t}>
                  <input
                    type="checkbox"
                    checked={typeOfIssue.includes(t)}
                    onChange={() => toggleIssueType(t)}
                  />
                  <span>{t}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="field-group">
            <label>Who does this impact?</label>
            <div className="radio-group">
              {WHO_IMPACTS.map((w) => (
                <label key={w}>
                  <input
                    type="radio"
                    name="who_impacts"
                    checked={whoImpacts === w}
                    onChange={() => setWhoImpacts(w)}
                  />
                  <span>{w}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="btn-row">
            <button type="button" className="btn-secondary" onClick={() => setStep(2)}>
              Back
            </button>
            <button type="submit" className="btn-primary" disabled={!priority || loading}>
              {loading ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </>
      )}

      {step === 3 && submissionType === 'enhancement' && (
        <>
          <h3 className="font-display font-bold text-lg uppercase text-white mb-4">Request Enhancement</h3>
          <div className="field-group">
            <label htmlFor="hub-priority-e">Priority *</label>
            <select
              id="hub-priority-e"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              required
            >
              <option value="">Select</option>
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          <div className="field-group">
            <label htmlFor="hub-business-purpose">What is the business purpose of this enhancement? *</label>
            <textarea
              id="hub-business-purpose"
              value={businessPurpose}
              onChange={(e) => setBusinessPurpose(e.target.value)}
              rows={3}
              required
            />
            <p className="helper">Describe what you are trying to do. (The more detail the better!)</p>
          </div>
          <div className="field-group">
            <label htmlFor="hub-business-impact">What is the business impact of this enhancement? *</label>
            <textarea
              id="hub-business-impact"
              value={businessImpact}
              onChange={(e) => setBusinessImpact(e.target.value)}
              rows={3}
              required
            />
            <p className="helper">(Time savings, customer impact, employee impact, etc.)</p>
          </div>
          <div className="field-group">
            <label htmlFor="hub-url-nav">What is the URL impacted by this enhancement, or will it be a new item on the left side navigation bar? Please specify the section (Administrative, Customer, Employee, etc.) *</label>
            <textarea
              id="hub-url-nav"
              value={urlNavImpact}
              onChange={(e) => setUrlNavImpact(e.target.value)}
              rows={2}
              required
            />
          </div>
          <div className="field-group">
            <label htmlFor="hub-feature-by">I would like this feature by this day *</label>
            <input
              id="hub-feature-by"
              type="datetime-local"
              value={featureByDate}
              onChange={(e) => setFeatureByDate(e.target.value)}
              required
            />
          </div>
          <div className="field-group">
            <label htmlFor="hub-who-testing">Who will be testing this new enhancement? *</label>
            <input
              id="hub-who-testing"
              type="text"
              value={whoTesting}
              onChange={(e) => setWhoTesting(e.target.value)}
              required
            />
          </div>
          <div className="field-group">
            <label htmlFor="hub-additional-notes">Any Additional Notes or Comments on this enhancement request?</label>
            <textarea
              id="hub-additional-notes"
              value={additionalNotes}
              onChange={(e) => setAdditionalNotes(e.target.value)}
              rows={3}
            />
          </div>
          <div className="field-group">
            <label>What sections of the hub does this enhancement impact?</label>
            <div className="checkbox-group">
              {HUB_SECTIONS.map((s) => (
                <label key={s}>
                  <input
                    type="checkbox"
                    checked={hubSections.includes(s)}
                    onChange={() => toggleHubSection(s)}
                  />
                  <span>{s}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="btn-row">
            <button type="button" className="btn-secondary" onClick={() => setStep(2)}>
              Back
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={!canSubmitEnhancement || loading}
            >
              {loading ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </>
      )}
    </form>
  )
}
